import {
	type EventEmitter,
	type JSX,
	Component,
	Event,
	getAssetPath,
	h,
	Host,
	Prop,
	State,
} from "@stencil/core";
import type { BodyType, Series } from "@phn/entities/content";
import { getConfig, isMobile } from "@phn/utility/helper";
import { navStateStore } from "@phn/state/nav-state-store";
import type { tagColor } from "../model-tags/model-tags";

// aspect ratio of placeholder image
const DEFAULT_ASPECT_RATIO = "160/47";

@Component({
	tag: "phn-model-tile",
	styleUrl: "model-tile.scss",
	scoped: true,
})
export class ModelTile {
	@Prop() breakpoint!: number;

	@Prop() modelSeries: Series | BodyType;

	@Prop() aspectRatio = DEFAULT_ASPECT_RATIO;

	@Prop() srcSet: string;

	@State() tagColor: tagColor = "light";

	@State() isFocused = false;

	@State() isHovered = false;

	@Event() imageLoad: EventEmitter<string>;

	@Event() imageLoadError: EventEmitter<string>;

	imageLoadHandler(id: string) {
		this.imageLoad.emit(id);
	}

	imageErrorHandler(id: string) {
		this.imageLoadError.emit(id);
		this.srcSet = this.getFallbackSrcSet();
	}

	private getFallbackSrcSet(): string {
		const assetsUrl = getConfig(navStateStore.state.env).ASSETS_URL;
		const assetPath = getAssetPath(`${assetsUrl}/series-assets/all`);
		return `${assetPath}/placeholder.webp, ${assetPath}/placeholder@2x.webp 2x`;
	}

	private handleFocusChange(focusIn: boolean) {
		this.isFocused = focusIn;
		const tagColor = this.isFocused ? "dark" : "light";
		this.setTagColor(tagColor);
	}

	private handleHover(hovered: boolean) {
		this.isHovered = hovered;
		const tagColor = this.isHovered || this.isFocused ? "dark" : "light";
		this.setTagColor(tagColor);
	}

	private setTagColor(color: tagColor) {
		if (isMobile(this.breakpoint)) return;
		this.tagColor = color;
	}

	private getNewTag(): string | undefined {
		if (!("topTag" in this.modelSeries)) return undefined;
		const currentDate = new Date();
		currentDate.setHours(0, 0, 0, 0);
		return currentDate <= new Date(this.modelSeries.topTag.validUntil)
			? this.modelSeries.topTag.text
			: undefined;
	}

	render(): JSX.Element {
		return (
			<Host>
				<div
					class="model-area-button"
					onMouseEnter={() => this.handleHover(true)}
					onMouseLeave={() => this.handleHover(false)}
					onFocusin={() => this.handleFocusChange(true)}
					onFocusout={() => this.handleFocusChange(false)}
				>
					<div class="model-content">
						<div class="model-header">
							<phn-p-heading size="medium" tag="h3" dir="auto">
								{this.modelSeries.name}
							</phn-p-heading>
							{this.getNewTag() && (
								<phn-p-tag compact={true} color="notification-info-soft">
									{this.getNewTag()}
								</phn-p-tag>
							)}
						</div>
						{"modelsAmount" in this.modelSeries && (
							<phn-p-text color="contrast-medium">
								{(this.modelSeries as BodyType).modelsAmount}
							</phn-p-text>
						)}
						<div
							style={{ aspectRatio: this.aspectRatio }}
							class="model-image-container"
						>
							<div class="model-image-container-ratio-content">
								<img
									class="model-image"
									srcSet={this.srcSet}
									alt=""
									onLoad={() => this.imageLoadHandler(this.modelSeries.id)}
									onError={() => this.imageErrorHandler(this.modelSeries.id)}
								/>
							</div>
						</div>
						<div class="model-tags">
							<phn-model-tags
								breakpoint={this.breakpoint}
								data-test-id="model-tags"
								tagColor={this.tagColor}
								modelTags={this.modelSeries.tags}
							/>
						</div>
					</div>
				</div>
			</Host>
		);
	}
}
